import { useQuery } from '@tanstack/react-query';
import { customInstance } from '@edison/api-client-react';
import { Link } from 'wouter';
import { AlertCircle, TriangleAlert } from 'lucide-react';
import {
  rdShareParts,
  CLAIM_MODE_LABELS,
  MODE_META,
  type ApportionmentView,
} from '@/pages/expenditure/methodology-card.js';

// The GET /api/dashboard/payroll-cost response body (data envelope contents). Exported so the
// dashboard's usePayrollCost hook and this panel share one contract.
export interface PayrollCostData {
  payrollTotal: number;
  grossWagesTotal: number;
  superTotal: number;
  workersCompTotal: number;
  onCostsTotal: number;
  leaveAccrualTotal: number;
  leaveBasis?: 'actual_bs' | 'estimated_4_52';
  workersCompConfigured: number | null;
  workersCompBasis: 'ledger' | 'configured' | 'none';
  provenance?: {
    wages: { accounts: string[]; monthsCovered: string[]; lastSyncedAt: string | null };
    onCosts: { accounts: string[] };
    leave: { basis: string; note: string };
    paygVerification: {
      verified: boolean;
      months: Array<{ period: string; wagesMonth: number; paygPayableBalance: number }>;
      note: string;
    };
  };
  byMonth: Array<{ period: string; loadedCost: number }>;
}

// Exact-dollar formatting: this panel is the defensibility record for the payroll figure, so it
// shows full amounts (the dashboard's abbreviated $962k style is for glanceable stats only).
function formatAUD(amount: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  }).format(amount);
}

// The 12 FY calendar months, 'YYYY-MM', July → June (mirror of reconciliation.ts fyMonths).
export function fyMonthKeys(incomeYear: string): string[] {
  const endYear = parseInt(incomeYear.slice(2), 10);
  if (!Number.isFinite(endYear)) return [];
  const startYear = endYear - 1;
  const out: string[] = [];
  for (let i = 0; i < 12; i++) {
    const mIdx = 6 + i; // 6 = July
    const y = startYear + Math.floor(mIdx / 12);
    const mm = (mIdx % 12) + 1;
    out.push(`${y}-${String(mm).padStart(2, '0')}`);
  }
  return out;
}

function monthName(yyyyMm: string): string {
  const [y, m] = yyyyMm.split('-');
  const d = new Date(Date.UTC(parseInt(y ?? '0', 10), parseInt(m ?? '1', 10) - 1, 1));
  return d.toLocaleDateString('en-AU', { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

function useApportionment(incomeYear: string) {
  // Same queryKey as the expenditure page — the two surfaces share one cached view.
  return useQuery({
    queryKey: ['expenditure-apportionment', incomeYear],
    queryFn: () =>
      customInstance<{ data: ApportionmentView }>({
        url: '/api/expenditure/apportionment',
        method: 'GET',
        params: { incomeYear },
      }).then((r) => r.data),
    staleTime: 5 * 60 * 1000,
    enabled: !!incomeYear,
  });
}

/**
 * "How is payroll cost calculated?" provenance panel — the R&DTI defensibility record behind the
 * payroll pill. Four grouped sections: ledger components (with sum row), coverage & PAYG
 * verification, the R&D derivation (verbatim-shared with the expenditure methodology card), and
 * the non-labour tie-in. Rule Zero: every figure comes from the real payload; anything
 * unavailable renders '—' or an explicit cannot-compute note.
 */
export function PayrollProvenancePanel({
  incomeYear,
  payrollCost,
  totalNonLabour,
}: {
  incomeYear: string;
  payrollCost: PayrollCostData;
  totalNonLabour: number;
}) {
  const apportionment = useApportionment(incomeYear);
  const prov = payrollCost.provenance;
  if (!prov) return null;

  // Coverage — derived from byMonth periods vs the FY month list, so the count and the named
  // missing months can never disagree.
  const fyKeys = fyMonthKeys(incomeYear);
  const syncedSet = new Set(payrollCost.byMonth.map((m) => m.period));
  const syncedCount = fyKeys.filter((k) => syncedSet.has(k)).length;
  const missingMonths = fyKeys.filter((k) => !syncedSet.has(k));

  const view = apportionment.data;
  const derivation = view ? rdShareParts(view.computed) : null;
  const claimModeLabel = view
    ? (CLAIM_MODE_LABELS[view.computed.claimMode] ?? view.computed.claimMode)
    : null;

  return (
    <details className="mt-2 rounded-md border bg-muted/30">
      <summary className="cursor-pointer select-none px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground">
        How is payroll cost calculated? Sources &amp; verification
      </summary>
      <div className="px-3 pb-3 space-y-4 text-xs">
        {/* ── 1. Ledger components ─────────────────────────────────────────── */}
        <div>
          <p className="font-medium mb-1">1. Ledger components (financial year, from synced Xero data)</p>
          <table className="w-full tabular-nums">
            <tbody>
              <tr className="border-b border-border/50">
                <td className="py-1 text-muted-foreground">Gross wages — incl. PAYG withheld</td>
                <td className="py-1 text-right font-mono">{formatAUD(payrollCost.grossWagesTotal)}</td>
                <td className="py-1 pl-3 text-muted-foreground">Xero P&amp;L {prov.wages.accounts.join(', ') || 'wage/salary accounts'} · {prov.wages.monthsCovered.length} months</td>
              </tr>
              <tr className="border-b border-border/50">
                <td className="py-1 text-muted-foreground">Superannuation</td>
                <td className="py-1 text-right font-mono">{formatAUD(payrollCost.superTotal)}</td>
                <td className="py-1 pl-3 text-muted-foreground">Xero P&amp;L {prov.onCosts.accounts.filter((a) => /super/i.test(a)).join(', ') || '—'}</td>
              </tr>
              <tr className="border-b border-border/50">
                <td className="py-1 text-muted-foreground">Workers comp</td>
                <td className="py-1 text-right font-mono">
                  {payrollCost.workersCompBasis === 'ledger'
                    ? formatAUD(payrollCost.workersCompTotal)
                    : payrollCost.workersCompBasis === 'configured' && payrollCost.workersCompConfigured != null
                      ? formatAUD(payrollCost.workersCompConfigured)
                      : '— (set premium in Integrations → Xero)'}
                </td>
                <td className="py-1 pl-3 text-muted-foreground">
                  {payrollCost.workersCompBasis === 'ledger'
                    ? prov.onCosts.accounts.filter((a) => !/super/i.test(a)).join(', ')
                    : payrollCost.workersCompBasis === 'configured' && payrollCost.workersCompConfigured != null
                      ? 'configured premium — inside Insurance in ledger; display only, not in totals'
                      : 'no named account (inside general Insurance, not separable)'}
                </td>
              </tr>
              <tr className="border-b border-border/50">
                <td className="py-1 text-muted-foreground">{payrollCost.leaveBasis === 'actual_bs' ? 'Leave liability movement' : 'Accrued leave (estimate)'}</td>
                <td className="py-1 text-right font-mono">{formatAUD(payrollCost.leaveAccrualTotal)}</td>
                <td className="py-1 pl-3 text-muted-foreground">{prov.leave.note}</td>
              </tr>
              <tr>
                <td className="py-1 font-medium">Total payroll cost</td>
                <td className="py-1 text-right font-mono font-semibold">{formatAUD(payrollCost.payrollTotal)}</td>
                <td className="py-1 pl-3 text-muted-foreground">wages + super + workers comp (ledger) + leave — the loaded payroll figure shown above</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* ── 2. Coverage & verification ───────────────────────────────────── */}
        <div>
          <p className="font-medium mb-1">2. Coverage &amp; verification</p>
          <p className="text-muted-foreground">
            Xero payroll months synced: <span className="font-mono text-foreground">{syncedCount}/12</span>
            {missingMonths.length > 0
              ? <> — missing: {missingMonths.map(monthName).join(', ')}</>
              : ' — full financial year covered'}
            {prov.wages.lastSyncedAt
              ? <> · last synced {new Date(prov.wages.lastSyncedAt).toLocaleString('en-AU')}</>
              : ' · last sync time unavailable'}
          </p>
          <div className="mt-2">
            <p className="font-medium mb-1">
              PAYG withholding — {prov.paygVerification.verified ? 'ledger-verified inside gross wages' : 'verification pending sync'}
            </p>
            <p className="text-muted-foreground mb-2">{prov.paygVerification.note}</p>
            {prov.paygVerification.months.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full tabular-nums">
                  <thead>
                    <tr className="text-muted-foreground border-b border-border/50">
                      <th className="py-1 text-left font-medium">Month</th>
                      <th className="py-1 text-right font-medium">Wages posted (gross)</th>
                      <th className="py-1 text-right font-medium">PAYG payable at month end</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prov.paygVerification.months.map((m) => (
                      <tr key={m.period} className="border-b border-border/30">
                        <td className="py-0.5 font-mono">{m.period}</td>
                        <td className="py-0.5 text-right font-mono">{formatAUD(m.wagesMonth)}</td>
                        <td className="py-0.5 text-right font-mono">{m.paygPayableBalance > 0 ? formatAUD(m.paygPayableBalance) : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* ── 3. R&D derivation ────────────────────────────────────────────── */}
        <div>
          <p className="font-medium mb-1">3. R&amp;D derivation</p>
          {apportionment.isLoading ? (
            <div className="h-8 w-full rounded skeleton" />
          ) : apportionment.error || !view ? (
            <p className="text-destructive flex items-center gap-1">
              <AlertCircle className="h-3 w-3 shrink-0" aria-hidden="true" />
              Failed to load the R&amp;D derivation.{' '}
              <button onClick={() => void apportionment.refetch()} className="underline">Retry</button>
            </p>
          ) : derivation ? (
            <div className="space-y-1">
              <p className="text-muted-foreground">
                Claim mode: <span className="text-foreground">{claimModeLabel}</span> · R&amp;D labour
                expenditure (capped, classified): <span className="font-mono text-foreground">{formatAUD(view.computed.rdLabour)}</span>
              </p>
              <div className="rounded-md border bg-muted/30 px-3 py-2 font-mono text-foreground/90">
                {derivation.prefix}
                <span className="font-semibold">{derivation.percentText}</span>
              </div>
            </div>
          ) : (
            <p className="flex items-start gap-1.5 text-amber-800 dark:text-amber-300">
              <TriangleAlert className="h-3.5 w-3.5 shrink-0 mt-0.5 text-amber-600" aria-hidden="true" />
              Cannot compute the R&amp;D share — the ratio needs synced Xero payroll for {incomeYear}.
              {claimModeLabel && <> Claim mode: {claimModeLabel}.</>}
            </p>
          )}
        </div>

        {/* ── 4. Non-labour tie-in ─────────────────────────────────────────── */}
        <div>
          <p className="font-medium mb-1">4. Non-labour tie-in</p>
          <p className="text-muted-foreground">
            Non-labour claimable total ({incomeYear}): <span className="font-mono text-foreground">{formatAUD(totalNonLabour)}</span>
            {view && (
              <> · overhead apportionment mode: <span className="text-foreground">{MODE_META[view.mode].label}</span></>
            )}
            {' · '}
            <Link href="/expenditure" className="underline hover:no-underline text-foreground">Methodology →</Link>
          </p>
        </div>

        <p className="text-muted-foreground">
          Methodology: ATO R&amp;DTI salary expenditure includes salaries/wages, leave, superannuation, payroll tax and workers comp. Payroll tax: nil (WA taxable wages under the $1M threshold). All figures derive from synced Xero data — nothing is entered by hand.
        </p>
      </div>
    </details>
  );
}
